import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, X, Printer } from "lucide-react";

let _setPreviewState = null;

/**
 * Affiche un PDF (Blob) dans une modale plein-écran avec un iframe.
 * Fonctionne dans toutes les configurations (Firefox iframe, Chrome, Safari).
 * L'utilisateur peut télécharger via le bouton dédié ou imprimer via les
 * contrôles natifs du viewer PDF.
 *
 * Usage : import { showPdfPreview } from "../components/PdfPreview";
 * showPdfPreview(blob, "etiquettes.pdf");
 */
export function showPdfPreview(blob, filename) {
  if (!_setPreviewState) {
    console.warn("PdfPreviewProvider not mounted yet");
    return;
  }
  const url = URL.createObjectURL(blob);
  _setPreviewState({ url, filename, blob });
}

export function PdfPreviewProvider({ children }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    _setPreviewState = setState;
    return () => { _setPreviewState = null; };
  }, []);

  const close = useCallback(() => {
    if (state?.url) URL.revokeObjectURL(state.url);
    setState(null);
  }, [state]);

  const print = useCallback(() => {
    if (!state) return;
    // Ouvre le PDF dans un nouvel onglet où l'utilisateur peut imprimer
    // (l'impression d'iframe blob est souvent bloquée cross-origin)
    const w = window.open(state.url, "_blank", "noopener,noreferrer");
    if (!w) {
      alert("Le navigateur a bloqué l'ouverture. Cliquez sur 'Télécharger' puis ouvrez le fichier pour l'imprimer.");
    }
  }, [state]);

  return (
    <>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="pdf-preview-dialog">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E5E5E0] bg-[#F9F9F6] shrink-0">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Aperçu PDF</DialogTitle>
              <DialogDescription className="text-sm font-mono text-[#1A1C1A] truncate">{state?.filename}</DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={print} data-testid="pdf-preview-print">
              <Printer className="w-4 h-4 mr-2" /> Imprimer
            </Button>
            {/* Vrai lien <a> avec attribut download : interaction utilisateur directe non bloquée par Firefox iframe */}
            {state?.url && (
              <a
                href={state.url}
                download={state.filename}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-[#1F4E3D] hover:bg-[#173B2E] text-white h-9 px-3"
                data-testid="pdf-preview-download"
              >
                <Download className="w-4 h-4 mr-2" /> Télécharger
              </a>
            )}
            <Button variant="ghost" size="icon" onClick={close} data-testid="pdf-preview-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex-1 bg-[#3A3A3A] overflow-hidden">
            {state?.url && (
              <iframe
                id="pdf-preview-iframe"
                src={state.url}
                title={state.filename || "Aperçu PDF"}
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
